import { z } from 'zod';

/**
 * Runtime configuration, validated at startup.
 *
 * A missing variable must fail loudly and immediately with a readable message,
 * not surface much later as an unexplained Firebase error. These values are not
 * secrets — they ship inside every web app. The protection comes from the
 * security rules, never from hiding the API key.
 */
const envSchema = z.object({
  VITE_FIREBASE_API_KEY: z.string().min(1, 'VITE_FIREBASE_API_KEY fehlt'),
  VITE_FIREBASE_AUTH_DOMAIN: z.string().min(1, 'VITE_FIREBASE_AUTH_DOMAIN fehlt'),
  VITE_FIREBASE_PROJECT_ID: z.string().min(1, 'VITE_FIREBASE_PROJECT_ID fehlt'),
  VITE_FIREBASE_APP_ID: z.string().min(1, 'VITE_FIREBASE_APP_ID fehlt'),
  VITE_FIREBASE_STORAGE_BUCKET: z.string().default(''),
  VITE_FIREBASE_MESSAGING_SENDER_ID: z.string().default(''),

  /** Connects to the local emulators instead of the real project. */
  VITE_USE_FIREBASE_EMULATORS: z
    .enum(['true', 'false'])
    .default('false')
    .transform((value) => value === 'true'),

  /** Which tournament this deployment shows. Prepares multiple tournaments. */
  VITE_TOURNAMENT_ID: z.string().min(1, 'VITE_TOURNAMENT_ID fehlt'),
});

function formatIssues(error: z.ZodError): string {
  return error.issues.map((issue) => `  • ${issue.message}`).join('\n');
}

function readEnv() {
  const result = envSchema.safeParse(import.meta.env);

  if (!result.success) {
    throw new Error(
      `Die Umgebungsvariablen sind unvollständig:\n${formatIssues(result.error)}\n\n` +
        'Bitte .env.local anhand von .env.example anlegen — siehe docs/SETUP-FIREBASE.md.',
    );
  }

  return result.data;
}

const parsed = readEnv();

export const env = {
  tournamentId: parsed.VITE_TOURNAMENT_ID,
  useEmulators: parsed.VITE_USE_FIREBASE_EMULATORS,
  firebase: {
    apiKey: parsed.VITE_FIREBASE_API_KEY,
    authDomain: parsed.VITE_FIREBASE_AUTH_DOMAIN,
    projectId: parsed.VITE_FIREBASE_PROJECT_ID,
    storageBucket: parsed.VITE_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: parsed.VITE_FIREBASE_MESSAGING_SENDER_ID,
    appId: parsed.VITE_FIREBASE_APP_ID,
  },
} as const;
